import React, { useRef } from "react";
import People from "../../assets/images/peoples.png";
import "./SmileGallery.css";

/*
 * SmileGallery - the six smile photographs as a scrolling reference strip.
 *
 * peoples.png is not six files, it is ONE 672x344 sheet holding a 3x2 grid
 * with gutters between the cells. Each tile shows the sheet as a background
 * scaled up and offset so exactly one cell lands in its box. The numbers
 * below were measured off the file rather than guessed, by scanning for the
 * fully-transparent gutter rows and columns: those came back at
 * x = 220..225 and 446..451, y = 167..175. So the cells are 220x167 and the
 * pitch between them is 226 across and 176 down.
 *
 * The crop is done with PERCENTAGES, not pixels, which is what lets a tile
 * be a fluid box rather than a fixed size. It works out cleanly because the
 * sheet is 672 wide and (672 - 220) / 2 = 226, exactly the column pitch, so
 * a background-position-x of 0% / 50% / 100% lands on columns 0 / 1 / 2. The
 * vertical case is off by a single source pixel (344 - 167 = 177 against a
 * 176 pitch), which is not visible.
 */

/* Left to right: the three close smiles, then the three portraits. */
const ORDER = [1, 0, 2, 4, 3, 5];

function Arrow({ direction }) {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <path
        d={
          direction === "left"
            ? "M13 8H3.5M7 3.5 2.5 8 7 12.5"
            : "M3 8h9.5M9 3.5 13.5 8 9 12.5"
        }
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function SmileGallery() {
  const trackRef = useRef(null);

  /* Scrolls by one viewport of the track rather than a magic pixel count, so
     the step stays right at every breakpoint. These arrows do something on
     click - a control that looks interactive and answers nothing is worse
     than no control. */
  const nudge = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    track.scrollBy({
      left: dir * Math.max(track.clientWidth * 0.8, 200),
      behavior: reduced ? "auto" : "smooth",
    });
  };

  return (
    <section className="smile-gallery">
      <div className="smile-gallery-head">
        <div>
          <h2 className="smile-gallery-title">Visual reference</h2>
          {/*
            No per-image labels. The reference mock tagged each photograph
            with a mould code ("Ovoid / 1M1", "Square / A2"); these are stock
            portraits with no mould behind them, so those codes would be
            invented clinical data on a page whose whole job is accurate
            measurements.
          */}
          <p className="smile-gallery-sub">
            Anterior work across a range of faces and smile lines.
          </p>
        </div>

        <div className="smile-gallery-nav">
          <button
            type="button"
            className="smile-gallery-btn"
            onClick={() => nudge(-1)}
            aria-label="Scroll reference images left"
          >
            <Arrow direction="left" />
          </button>
          <button
            type="button"
            className="smile-gallery-btn"
            onClick={() => nudge(1)}
            aria-label="Scroll reference images right"
          >
            <Arrow direction="right" />
          </button>
        </div>
      </div>

      <div className="smile-gallery-track" ref={trackRef}>
        {ORDER.map((cell) => {
          const col = cell % 3;
          const row = Math.floor(cell / 3);
          return (
            <span
              key={cell}
              className="smile-gallery-tile"
              aria-hidden="true"
              style={{
                backgroundImage: `url(${People})`,
                backgroundPosition: `${col * 50}% ${row * 100}%`,
              }}
            />
          );
        })}
      </div>
    </section>
  );
}
